import React from "react";
import { HomeIcon, CalendarIcon, UserIcon, LinkIcon, Building2Icon, SettingsIcon, Lightbulb, GaugeIcon, StethoscopeIcon, CreditCardIcon, UsersIcon, FileTextIcon, TrophyIcon, Hammer, BarChart3, Camera, Boxes, Calculator, HelpCircle, Video, Eye } from "lucide-react";
import { Icon } from "lucide-react";
import { horseHead } from '@lucide/lab';
import { Link } from "react-router-dom";
import { useSubscription } from "../hooks/useSubscription";
import { useUnreadMessages } from "../hooks/useUnreadMessages";
import { useConnectNotifications } from "../hooks/useConnectNotifications";

export default function Sidebar({ onNavigate }) {
  const { hasFeature, getTierName, subscription, isLoading } = useSubscription();
  const { unreadCount } = useUnreadMessages();
  const { totalNotificationCount } = useConnectNotifications();
  const linkClass = "flex items-center px-4 py-3 lg:py-2 hover:bg-kci-200 rounded-lg transition-colors";

  const handleNavigation = () => {
    if (onNavigate) {
      onNavigate();
    }
  };

  return (
    <div className="h-full p-4 bg-kci-50 lg:bg-kci-50 lg:shadow-none shadow-lg flex flex-col">
      {/* Mobile header - only visible on mobile */}
      <div className="lg:hidden mb-4 pb-4 border-b border-kci-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-kci-900">Menu</h2>
            <p className="text-sm text-kci-600">Navigation</p>
          </div>
          <button
            onClick={handleNavigation}
            className="p-2 text-kci-500 hover:text-kci-700 hover:bg-kci-200 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      <div className="space-y-2 flex-1 overflow-y-auto">
        {/* Essentials Section Header */}
        <div className="text-xs font-semibold text-kci-500 px-4 mb-1">Essentials</div>

        <Link to="/" className={linkClass} onClick={handleNavigation}><HomeIcon className="mr-2" /> Home</Link>
        <Link to="/calendar" className={linkClass} onClick={handleNavigation}><CalendarIcon className="mr-2" /> Calendar</Link>      
        <Link to="/horses" className={linkClass} onClick={handleNavigation}><Icon iconNode={horseHead} className="mr-2" /> Horses</Link>
        
        {hasFeature('veterinarians') && (
          <Link to="/veterinarians" className={linkClass} onClick={handleNavigation}><StethoscopeIcon className="mr-2" /> Veterinarians</Link>
        )}
        
        {hasFeature('farriers') && (
          <Link to="/farriers" className={linkClass} onClick={handleNavigation}><Hammer className="mr-2" /> Farriers</Link>
        )}
        
        {/* Supplies - available to all plans for cost tracking */}
        <Link to="/supplies" className={linkClass} onClick={handleNavigation}><Boxes className="mr-2" /> Supplies</Link>
        
        {/* Enhanced Tier Features */}
        {hasFeature('connect') && (
          <Link to="/connect" className={linkClass} onClick={handleNavigation}>
            <LinkIcon className="mr-2" /> 
            Connect
            {totalNotificationCount > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs px-2 py-1 rounded-lg min-w-[20px] text-center">
                {totalNotificationCount}
              </span>
            )}
          </Link>
        )}
        
        {/* Divider between Essentials and Competition & Performance */}
        {(hasFeature('entries') || hasFeature('results') || hasFeature('memberships') || hasFeature('analytics') || hasFeature('insights') || hasFeature('liveresults') || hasFeature('score_simulator')) && (
          <div className="border-t border-kci-200 my-2"></div>
        )}

        {/* Competition & Performance Section Header */}
        {(hasFeature('entries') || hasFeature('results') || hasFeature('memberships') || hasFeature('analytics') || hasFeature('insights') || hasFeature('liveresults') || hasFeature('score_simulator')) && (
          <div className="text-xs font-semibold text-kci-500 px-4 mt-3 mb-1">Competition & Performance</div>
        )}

        {/* Webhook Integration Features */}
        {hasFeature('entries') && (
          <Link to="/entries" className={linkClass} onClick={handleNavigation}><FileTextIcon className="mr-2" /> Entries</Link>
        )}
        
        {/* Premium Tier Features */}
        {hasFeature('results') && (
          <Link to="/results" className={linkClass} onClick={handleNavigation}><TrophyIcon className="mr-2" /> Results</Link>
        )}
               
        {hasFeature('memberships') && (
          <Link to="/memberships" className={linkClass} onClick={handleNavigation}><Building2Icon className="mr-2" /> Memberships</Link>
        )}
        
        {/* Analytics - Enhanced/Premium Feature */}
        {hasFeature('analytics') && (
          <Link to="/analytics" className={linkClass} onClick={handleNavigation}><BarChart3 className="mr-2" /> Analytics</Link>
        )}
        
        {/* Insights - Showpremium Feature */}
        {hasFeature('insights') && (
          <Link to="/insights" className={linkClass} onClick={handleNavigation}><Lightbulb className="mr-2" /> Insights</Link>
        )}
        
        {hasFeature('liveresults') && (
          <Link to="/liveresults" className={linkClass} onClick={handleNavigation}><GaugeIcon className="mr-2" /> Live Results</Link>
        )}

        {hasFeature('score_simulator') && (
          <Link to="/score-simulator" className={linkClass} onClick={handleNavigation}><Calculator className="mr-2" /> Score Simulator</Link>
        )}

        {/* Divider between Score Simulator and Media */}
        {hasFeature('score_simulator') && (hasFeature('rodeos') || hasFeature('live_streaming') || hasFeature('fan_viewing')) && (
          <div className="border-t border-kci-200 my-2"></div>
        )}

        {/* Media & Streaming Section Header */}
        {(hasFeature('rodeos') || hasFeature('live_streaming') || hasFeature('fan_viewing')) && (
          <div className="text-xs font-semibold text-kci-500 px-4 mt-3 mb-1">Media & Streaming</div>
        )}

        {hasFeature('rodeos') && (
          <Link to="/media" className={linkClass} onClick={handleNavigation}><Camera className="mr-2" /> Media</Link>
        )}
        
        {/* Live Streaming Features - moved below Media */}
        {hasFeature('live_streaming') && (
          <Link to="/streaming/live" className={linkClass} onClick={handleNavigation}>
            <Video className="mr-2" /> 
            Live Stream
          </Link>
        )}
        
        {hasFeature('fan_viewing') && (
          <Link to="/streaming/fan-view" className={linkClass} onClick={handleNavigation}>
            <Eye className="mr-2" />
            Fan View
          </Link>
        )}

        {/* Divider between Media & Streaming and Account & Support */}
        <div className="border-t border-kci-200 my-2"></div>

        {/* Account & Support Section Header */}
        <div className="text-xs font-semibold text-kci-500 px-4 mt-3 mb-1">Account & Support</div>

        {hasFeature('family_plan') && (
          <Link to="/family" className={linkClass} onClick={handleNavigation}><UsersIcon className="mr-2" /> Family</Link>
        )}

        <Link to="/profile" className={linkClass} onClick={handleNavigation}><UserIcon className="mr-2" /> Profile</Link>
        <Link to="/settings" className={linkClass} onClick={handleNavigation}><SettingsIcon className="mr-2" /> Settings</Link>

        {/* Subscription - moved to Account & Support section */}
        <Link to="/subscription" className={linkClass} onClick={handleNavigation}>
          <CreditCardIcon className="mr-2" /> Subscription
        </Link>

        <Link to="/help" className={linkClass} onClick={handleNavigation}><HelpCircle className="mr-2" /> Help</Link>

        {/* Divider before logout */}
        <div className="border-t border-kci-200 my-2"></div>
        
        {/* Logout Button */}
        <button
          onClick={() => {
            localStorage.removeItem("token");
            window.location.href = "/";
          }}
          className="w-full flex items-center px-4 py-3 lg:py-2 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Logout
        </button>
        
        {/* Mobile placeholder to prevent logout button from being hidden behind bottom panels */}
        <div className="h-16 lg:hidden"></div>
      </div>
    </div>
  );
}
